import React, { useEffect, useMemo, useState } from 'react';

import { useApolloClient } from '@apollo/client/react';
import { Autocomplete, Box, Skeleton, Stack, Typography } from '@mui/material';
import { BarChart } from '@mui/x-charts-premium/BarChart';
import type { DataGridPremiumProps, GridColDef } from '@mui/x-data-grid-premium';
import { DataGridPremium, useGridApiRef } from '@mui/x-data-grid-premium';
import { AlineAutocompleteInput } from 'src/components';
import { AlineDatePicker } from 'src/components/generic';
import { ListCountFooter } from 'src/components/generic/data-grid/ListCountFooter';
import { useStable } from 'src/editor/helpers/useStable';
import { Apollo } from 'src/gql/generated';
import { createDataGridPremiumPaperStyles } from 'src/hooks';
import { makeStyles } from 'tss-react/mui';

import {
  formatDateInput,
  formatUsageNumber,
  formatUsageTimestamp,
  getDateFromInput,
  getDefaultUsageDateRange,
  getSeriesValue,
  getUsageDateLabels,
  getUsageDateRange,
  getUsageGroupLabel,
  getUsageModelLabel,
  getUsageSourceLabel,
  getUsageTimeZoneCopy,
  UsageRange,
} from './usage-data';

const useStyles = makeStyles()(theme => ({
  cardGrid: {
    display: 'grid',
    gap: theme.spacing(2),
    gridTemplateColumns: 'repeat(3, minmax(160px, 1fr))',
  },
  chartContainer: {
    border: `1px solid ${theme.appColors.rule}`,
    borderRadius: 12,
    padding: theme.spacing(2),
  },
  page: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    padding: theme.spacing(2),
  },
  tableContainer: {
    height: 640,
  },
}));

const useFilterStyles = makeStyles()(theme => ({
  dateField: {
    width: 220,
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  label: {
    color: theme.palette.text.secondary,
    fontSize: 12,
  },
  select: {
    minWidth: 140,
  },
}));

type AdminAiUsageDetailRow =
  Apollo.AdminAiUsageDetailRowsQuery['adminAiUsageDetailRows']['rows'][number];
type AdminAiUsageTableRow = {
  createdAt: AdminAiUsageDetailRow['createdAt'];
  id: string;
  modelKey: AdminAiUsageDetailRow['modelKey'];
  source: AdminAiUsageDetailRow['source'];
  totalTokensUsed: number;
  user: AdminAiUsageDetailRow['user'];
};

const GROUP_BY_OPTIONS = [
  { label: 'None', value: Apollo.AdminAiUsageGroupBy.None },
  { label: 'User', value: Apollo.AdminAiUsageGroupBy.User },
  { label: 'Model', value: Apollo.AdminAiUsageGroupBy.Model },
  { label: 'Source', value: Apollo.AdminAiUsageGroupBy.Source },
];

const RANGE_OPTIONS = [
  { label: 'Last 7 days', value: UsageRange.Last7Days },
  { label: 'Last 30 days', value: UsageRange.Last30Days },
  { label: 'Last 90 days', value: UsageRange.Last90Days },
  { label: 'Billing period', value: UsageRange.CurrentBillingPeriod },
  { label: 'Custom', value: UsageRange.Custom },
];

export function UsagePage(): JSX.Element {
  const apiRef = useGridApiRef();
  const client = useApolloClient();
  const { classes, theme } = useStyles();
  const defaultRange = getDefaultUsageDateRange();
  const [range, setRange] = useState<UsageRange>(UsageRange.Last30Days);
  const [startDate, setStartDate] = useState(defaultRange.startDate);
  const [endDate, setEndDate] = useState(defaultRange.endDate);
  const [groupBy, setGroupBy] = useState(Apollo.AdminAiUsageGroupBy.User);
  const [detailRowCount, setDetailRowCount] = useState(0);
  const [startDateOpen, setStartDateOpen] = useState(false);
  const [endDateOpen, setEndDateOpen] = useState(false);
  const timeZone = useStable(() => Intl.DateTimeFormat().resolvedOptions().timeZone, []);
  const startDateValue = useMemo(() => getDateFromInput(startDate), [startDate]);
  const endDateValue = useMemo(() => getDateFromInput(endDate), [endDate]);
  const dateLabels = useMemo(
    () => getUsageDateLabels({ endDate, startDate }),
    [endDate, startDate]
  );
  const usageInput = useMemo(
    () => ({
      bucket: Apollo.AdminAiUsageBucket.Day,
      endDate,
      groupBy,
      startDate,
      timeZone,
    }),
    [endDate, groupBy, startDate, timeZone]
  );
  const { data, loading } = Apollo.useAdminAiUsageQuery({
    variables: {
      input: usageInput,
    },
  });
  const { data: currentUserData, loading: currentUserLoading } = Apollo.useCurrentUserQuery();
  const usage = data?.adminAiUsage;
  const currentUserAiUsage = currentUserData?.currentUser?.aiUsage;
  const remainingTokens = currentUserAiUsage
    ? Math.max(currentUserAiUsage.totalAvailableTokens - currentUserAiUsage.usedTokens, 0)
    : 0;
  const chart = useMemo(
    () =>
      buildChartData({
        dateLabels,
        groupBy,
        points: usage?.series ?? [],
      }),
    [dateLabels, groupBy, usage?.series]
  );
  const summaryLoading = loading || currentUserLoading;
  const columns = useMemo<GridColDef<AdminAiUsageTableRow>[]>(
    () => [
      {
        field: 'createdAt',
        flex: 0.9,
        headerName: 'Timestamp',
        valueFormatter: value => formatUsageTimestamp(value, timeZone),
      },
      {
        field: 'user',
        flex: 1.2,
        headerName: 'User',
        sortable: false,
        valueGetter: (_value, row) => row.user.displayName || row.user.email,
      },
      {
        field: 'source',
        flex: 1,
        headerName: 'Source',
        valueFormatter: value => getUsageSourceLabel(value),
      },
      {
        field: 'modelKey',
        flex: 1,
        headerName: 'Model',
        valueFormatter: value => getUsageModelLabel(value),
      },
      {
        field: 'totalTokensUsed',
        flex: 0.8,
        headerName: 'Tokens Used',
        valueFormatter: value => formatUsageNumber(value),
      },
    ],
    [timeZone]
  );
  const dataSource = useMemo<DataGridPremiumProps<AdminAiUsageTableRow>['dataSource']>(
    () => ({
      getRows: async params => {
        const offset = typeof params.start === 'number' ? params.start : 0;
        const limit =
          typeof params.start === 'number' && typeof params.end === 'number'
            ? params.end - params.start + 1
            : (params.paginationModel?.pageSize ?? 50);
        const { data } = await client.query<
          Apollo.AdminAiUsageDetailRowsQuery,
          Apollo.AdminAiUsageDetailRowsQueryVariables
        >({
          fetchPolicy: 'network-only',
          query: Apollo.AdminAiUsageDetailRowsDocument,
          variables: {
            input: {
              limit,
              offset,
              sortModel: params.sortModel,
              usageInput,
            },
          },
        });
        if (!data) throw new Error('Failed to load AI usage detail rows.');

        const result = data.adminAiUsageDetailRows;
        setDetailRowCount(result.totalCount);

        return {
          rowCount: result.totalCount,
          rows: buildDetailRows(result.rows),
        };
      },
    }),
    [client, usageInput]
  );

  useEffect(() => {
    apiRef.current?.dataSource?.cache?.clear();
    setDetailRowCount(0);
  }, [apiRef, usageInput]);

  return (
    <div className={classes.page}>
      <div>
        <Typography variant="h1">AI Usage</Typography>
        <Typography color="textSecondary" variant="caption">
          Track included AI usage by user, model, and source.
        </Typography>
      </div>

      <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 2 }}>
        <UsageSelect
          label="Range"
          options={RANGE_OPTIONS}
          value={range}
          onChange={nextRange => {
            setRange(nextRange);
            if (nextRange === UsageRange.Custom) return;
            const nextDates = getUsageDateRange(nextRange);
            setStartDate(nextDates.startDate);
            setEndDate(nextDates.endDate);
          }}
        />
        <UsageDatePicker
          label="Start"
          open={startDateOpen}
          setOpen={setStartDateOpen}
          value={startDateValue}
          onDateChange={date => {
            if (!date) return;
            setRange(UsageRange.Custom);
            setStartDate(formatDateInput(date));
          }}
        />
        <UsageDatePicker
          label="End"
          open={endDateOpen}
          setOpen={setEndDateOpen}
          value={endDateValue}
          onDateChange={date => {
            if (!date) return;
            setRange(UsageRange.Custom);
            setEndDate(formatDateInput(date));
          }}
        />
        <UsageSelect
          label="Group by"
          options={GROUP_BY_OPTIONS}
          value={groupBy}
          onChange={setGroupBy}
        />
      </Stack>

      <div className={classes.cardGrid}>
        <SummaryCard
          label="Used tokens"
          loading={summaryLoading}
          value={formatUsageNumber(currentUserAiUsage?.usedTokens ?? 0)}
        />
        <SummaryCard
          label="Available tokens"
          loading={summaryLoading}
          value={formatUsageNumber(currentUserAiUsage?.totalAvailableTokens ?? 0)}
        />
        <SummaryCard
          label="Remaining tokens"
          loading={summaryLoading}
          value={formatUsageNumber(remainingTokens)}
        />
      </div>

      <div className={classes.chartContainer}>
        <Typography gutterBottom variant="subtitle1">
          Usage over time
        </Typography>
        <Typography color="textSecondary" variant="caption">
          {getUsageTimeZoneCopy(timeZone)}
        </Typography>
        {loading ? (
          <Skeleton height={300} variant="rounded" />
        ) : (
          <BarChart
            height={300}
            margin={{ bottom: 60, left: 80, right: 20, top: 40 }}
            series={chart.series}
            xAxis={[{ data: chart.xLabels, label: 'Usage date', scaleType: 'band' }]}
            yAxis={[
              {
                label: 'Total tokens',
                valueFormatter: (value: number) => formatUsageNumber(value),
              },
            ]}
          />
        )}
      </div>

      <Box className={classes.tableContainer}>
        <DataGridPremium<AdminAiUsageTableRow>
          lazyLoading
          pagination
          apiRef={apiRef}
          columns={columns}
          dataSource={dataSource}
          density="compact"
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: 50 },
            },
            sorting: {
              sortModel: [{ field: 'createdAt', sort: 'desc' }],
            },
          }}
          rowCount={detailRowCount}
          slotProps={{
            footer: {
              noun: 'usage records',
              totalCount: detailRowCount,
            },
          }}
          slots={{ footer: ListCountFooter }}
          sx={createDataGridPremiumPaperStyles(theme)}
        />
      </Box>
    </div>
  );
}

const useSummaryCardStyles = makeStyles()(theme => ({
  card: {
    border: `1px solid ${theme.appColors.rule}`,
    borderRadius: 12,
    padding: theme.spacing(2),
  },
  text: {
    fontSize: 18,
  },
}));

function SummaryCard({
  label,
  loading,
  value,
}: {
  label: string;
  loading: boolean;
  value: string;
}) {
  const { classes } = useSummaryCardStyles();
  return (
    <div className={classes.card}>
      <Typography variant="subtitle1">{label}</Typography>
      {loading ? (
        <Skeleton height={24} variant="text" width="40%" />
      ) : (
        <Typography className={classes.text}>{value}</Typography>
      )}
    </div>
  );
}

function UsageDatePicker({
  label,
  onDateChange,
  open,
  setOpen,
  value,
}: {
  label: string;
  onDateChange: (date: Date | null) => void;
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  value: Date | null;
}) {
  const { classes } = useFilterStyles();
  return (
    <div className={classes.field}>
      <Typography className={classes.label}>{label}</Typography>
      <AlineDatePicker
        dateFieldProps={{ fullWidth: true }}
        inputClassName={classes.dateField}
        open={open}
        setOpen={setOpen}
        value={value}
        onDateChange={onDateChange}
      />
    </div>
  );
}

function UsageSelect<TValue extends string>({
  label,
  onChange,
  options,
  value,
}: {
  label: string;
  onChange: (value: TValue) => void;
  options: { label: string; value: TValue }[];
  value: TValue;
}) {
  const [open, setOpen] = useState(false);
  const { classes } = useFilterStyles();
  const selectedOption = options.find(option => option.value === value);

  return (
    <div className={classes.field}>
      <Typography className={classes.label}>{label}</Typography>
      <Autocomplete<{ label: string; value: TValue }, false, true, false>
        blurOnSelect
        disableClearable
        openOnFocus
        className={classes.select}
        getOptionLabel={option => option.label}
        isOptionEqualToValue={(option, selectedValue) => option.value === selectedValue.value}
        open={open}
        options={options}
        renderInput={params => <AlineAutocompleteInput {...params} />}
        value={selectedOption}
        onChange={(_event, nextOption) => onChange(nextOption.value)}
        onClose={() => setOpen(false)}
        onOpen={() => setOpen(true)}
      />
    </div>
  );
}

function buildDetailRows(rows: AdminAiUsageDetailRow[]) {
  return rows.map(row => ({
    createdAt: row.createdAt,
    id: row.id,
    modelKey: row.modelKey,
    source: row.source,
    totalTokensUsed: row.totalTokensUsed,
    user: row.user,
  }));
}

function buildChartData({
  dateLabels,
  groupBy,
  points,
}: {
  dateLabels: string[];
  groupBy: Apollo.AdminAiUsageGroupBy;
  points: Apollo.AdminAiUsageQuery['adminAiUsage']['series'];
}) {
  const xLabels = dateLabels;
  const pointLabels = [...new Set(points.map(point => getUsageGroupLabel({ groupBy, point })))];
  const groupLabels =
    groupBy === Apollo.AdminAiUsageGroupBy.None && pointLabels.length === 0
      ? ['Total']
      : pointLabels;
  const series = groupLabels.map(groupLabel => ({
    data: xLabels.map(dateLabel =>
      points
        .filter(
          item =>
            item.bucketStartDate === dateLabel &&
            getUsageGroupLabel({ groupBy, point: item }) === groupLabel
        )
        .reduce((total, point) => total + getSeriesValue(point), 0)
    ),
    label: groupLabel,
    stack: 'total',
  }));

  return { series, xLabels };
}
